import React from "react";
import { ArrowDownUp, Filter } from "lucide-react";
import { ActionMenu } from "../../components/molecules/ActionMenu";
import {
  ALL_DATA_STUDIO_SOURCES,
  useDataStudioTablesPanel,
} from "../../hooks/useDataStudioTablesPanel";
import type { DataStudioSortOption } from "../../hooks/useDataStudioTablesPanel";
import { usePortalMenu } from "../../hooks/usePortalMenu";
import { menus } from "../../components/registry";
import {
  EmptyMessage,
  HeadingCount,
  HeadingLabel,
  Panel,
  PanelHeading,
  SearchField,
  SearchIcon,
  SearchInput,
  SearchSection,
  SourceLogo,
  TableButton,
  TableList,
  TableListItem,
  TableNavigation,
  TableName,
  ToolbarIconButton,
  ToolbarMenuAnchor,
} from "./DataStudioSecondaryPanel.styles";
import type { DataStudioSecondaryPanelProps } from "./DataStudioSecondaryPanel.types";

export const DataStudioSecondaryPanel: React.FC<
  DataStudioSecondaryPanelProps
> = ({
  tables = [],
  total = 0,
  isLoading = false,
  errorMessage = null,
  searchTerm,
  onSearchTermChange,
  onSortOrderChange,
}) => {
  const {
    query,
    setQuery,
    selectedTableId,
    selectTable,
    visibleTables,
    sourceNames,
    openMenu,
    toggleMenu,
    closeMenus,
    selectSourceFilter,
    selectSortOption,
  } = useDataStudioTablesPanel(tables);
  const filterMenuOpen = openMenu === "filter";
  const sortMenuOpen = openMenu === "sort";
  const filterPortal = usePortalMenu<HTMLSpanElement, HTMLUListElement>({
    open: filterMenuOpen,
    onOutsideClick: closeMenus,
  });
  const sortPortal = usePortalMenu<HTMLSpanElement, HTMLUListElement>({
    open: sortMenuOpen,
    onOutsideClick: closeMenus,
  });
  const filterMenuItems = [
    { id: ALL_DATA_STUDIO_SOURCES, label: "All sources" },
    ...sourceNames.map((sourceName) => ({
      id: sourceName,
      label: sourceName,
    })),
  ];

  return (
    <Panel
      aria-label="Data studio secondary panel"
      data-testid="data-studio-secondary-panel"
    >
      <PanelHeading>
        <HeadingLabel>Ingested tables</HeadingLabel>
        <HeadingCount>({total})</HeadingCount>
      </PanelHeading>

      <SearchSection>
        <SearchField>
          <SearchIcon aria-hidden="true" />
          <SearchInput
            type="search"
            value={searchTerm ?? query}
            onChange={(event) => {
              setQuery(event.target.value);
              onSearchTermChange?.(event.target.value);
            }}
            placeholder="Find tables"
            aria-label="Find tables"
          />
        </SearchField>
        <ActionMenu
          items={filterMenuItems}
          onAction={selectSourceFilter}
          isOpen={filterMenuOpen}
          onClose={closeMenus}
          menuRef={filterPortal.menuRef}
          menuRect={filterPortal.rect}
          ariaLabel="Filter tables by data source"
          align="right"
          trigger={
            <ToolbarMenuAnchor ref={filterPortal.triggerRef}>
              <ToolbarIconButton
                icon={Filter}
                aria-label="Filter tables by data source"
                onClick={() => toggleMenu("filter")}
              />
            </ToolbarMenuAnchor>
          }
        />
        <ActionMenu<DataStudioSortOption>
          items={menus.dataStudioSort}
          onAction={(option) => {
            selectSortOption(option);
            onSortOrderChange?.(
              option === "alphabeticalDesc" ? "desc" : "asc",
            );
          }}
          isOpen={sortMenuOpen}
          onClose={closeMenus}
          menuRef={sortPortal.menuRef}
          menuRect={sortPortal.rect}
          ariaLabel="Sort tables"
          align="right"
          trigger={
            <ToolbarMenuAnchor ref={sortPortal.triggerRef}>
              <ToolbarIconButton
                icon={ArrowDownUp}
                aria-label="Sort tables"
                onClick={() => toggleMenu("sort")}
              />
            </ToolbarMenuAnchor>
          }
        />
      </SearchSection>

      {isLoading && visibleTables.length === 0 ? (
        <EmptyMessage role="status">Loading tables…</EmptyMessage>
      ) : errorMessage && visibleTables.length === 0 ? (
        <EmptyMessage role="alert">{errorMessage}</EmptyMessage>
      ) : visibleTables.length > 0 ? (
        <TableNavigation aria-label="Ingested tables">
          <TableList>
            {visibleTables.map((table) => {
              const selected = table.id === selectedTableId;

              return (
                <TableListItem key={table.id}>
                  <TableButton
                    type="button"
                    $selected={selected}
                    aria-current={selected ? "page" : undefined}
                    onClick={() => selectTable(table.id)}
                  >
                    {table.source.logoUrl ? (
                      <SourceLogo
                        src={table.source.logoUrl}
                        alt=""
                        aria-hidden="true"
                      />
                    ) : null}
                    <TableName>{table.label ?? table.name}</TableName>
                  </TableButton>
                </TableListItem>
              );
            })}
          </TableList>
        </TableNavigation>
      ) : (
        <EmptyMessage role="status">No tables found</EmptyMessage>
      )}
    </Panel>
  );
};

export default DataStudioSecondaryPanel;
